/**
 * Client Dashboard for Y GO Shuttle Services France
 * Espace client amélioré avec historique, suivi et gestion profil
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BookingDetailModal from '../components/client/BookingDetailModal';
import { 
  LogOut, 
  Calendar, 
  Clock, 
  MapPin,
  User,
  CreditCard,
  Plus,
  Car,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Phone,
  Mail,
  Edit2,
  Save,
  Home,
  History,
  Settings,
  FileText,
  X,
  Download,
  Euro
} from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import config from 'config';

const API_URL = config.API_URL;

// Status badge colors
const STATUS_COLORS = {
  draft: 'bg-gray-500/20 text-gray-400',
  pending_payment: 'bg-yellow-500/20 text-yellow-400',
  confirmed: 'bg-green-500/20 text-green-400',
  assigned: 'bg-blue-500/20 text-blue-400',
  driver_assigned: 'bg-blue-500/20 text-blue-400',
  driver_accepted: 'bg-indigo-500/20 text-indigo-400',
  accepted: 'bg-indigo-500/20 text-indigo-400',
  en_route: 'bg-orange-500/20 text-orange-400',
  waiting_for_pickup: 'bg-amber-500/20 text-amber-400',
  client_on_board: 'bg-cyan-500/20 text-cyan-400',
  dropped_off: 'bg-purple-500/20 text-purple-400',
  in_progress: 'bg-purple-500/20 text-purple-400',
  completed: 'bg-emerald-500/20 text-emerald-400',
  cancelled: 'bg-red-500/20 text-red-400',
  no_show: 'bg-red-500/20 text-red-400',
};

const STATUS_LABELS = {
  draft: 'Brouillon',
  pending_payment: 'En attente paiement',
  confirmed: 'Confirmé',
  assigned: 'Chauffeur assigné',
  driver_assigned: 'Chauffeur assigné',
  driver_accepted: 'Chauffeur confirmé',
  accepted: 'Chauffeur confirmé',
  en_route: 'Chauffeur en route',
  waiting_for_pickup: 'Chauffeur sur place',
  client_on_board: 'En course',
  dropped_off: 'Arrivé à destination',
  in_progress: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé',
  no_show: 'Client absent',
};

const ClientDashboard = () => {
  const { t } = useTranslation();
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, upcoming: 0, totalSpent: 0 });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || ''
  });
  const [saving, setSaving] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [clientDocuments, setClientDocuments] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [clientHistory, setClientHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch bookings function wrapped in useCallback
  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/bookings/my/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allBookings = response.data.bookings || [];
      setBookings(allBookings);
      
      // Filter upcoming bookings
      const now = new Date();
      const upcoming = allBookings.filter(b => 
        new Date(b.pickup_datetime) > now && 
        !['cancelled', 'completed'].includes(b.status)
      );
      setUpcomingBookings(upcoming);
      
      // Calculate stats
      const completed = allBookings.filter(b => b.status === 'completed').length;
      const totalSpent = allBookings
        .filter(b => b.status === 'completed')
        .reduce((sum, b) => sum + (b.price?.total || 0), 0);
      
      setStats({
        total: allBookings.length,
        completed,
        upcoming: upcoming.length,
        totalSpent
      });
      
      return allBookings;
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Load documents when tab changes
  const fetchDocuments = useCallback(async () => {
    try {
      setDocsLoading(true);
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API_URL}/api/client-portal/my-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientDocuments(res.data || []);
    } catch (err) { console.error('Fetch documents:', err); }
    finally { setDocsLoading(false); }
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      setPaymentsLoading(true);
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API_URL}/api/client-portal/my-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientPayments(res.data || []);
    } catch (err) { console.error('Fetch payments:', err); }
    finally { setPaymentsLoading(false); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API_URL}/api/client-portal/my-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientHistory(res.data || []);
    } catch (err) { console.error('Fetch history:', err); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'documents') fetchDocuments();
    if (activeTab === 'payments') fetchPayments();
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchDocuments, fetchPayments, fetchHistory]);
  
  // Handle Stripe payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentSuccess = searchParams.get('success');
    const paymentCancelled = searchParams.get('cancelled');
    
    if (sessionId || paymentSuccess || paymentCancelled) {
      // Clear URL params
      setSearchParams({});
      
      if (paymentSuccess === 'true' || sessionId) {
        setPaymentMessage({ type: 'success', text: 'Paiement effectué avec succès !' });
        // Force refresh bookings to get updated payment status
        fetchBookings();
        // Auto-clear message after 5 seconds
        setTimeout(() => setPaymentMessage(null), 5000);
      } else if (paymentCancelled === 'true') {
        setPaymentMessage({ type: 'warning', text: 'Paiement annulé' });
        setTimeout(() => setPaymentMessage(null), 5000);
      }
    }
  }, [searchParams, setSearchParams, fetchBookings]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await updateProfile(profileData);
      setEditMode(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-[#1F1F1F] border-b border-[#C9A24D]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <BrandMark size="lg" />
              </Link>
              <span className="text-[#E5E5E5]/40 text-sm hidden sm:inline">|</span>
              <span className="text-[#E5E5E5]/60 text-sm hidden sm:inline">{t('common.mySpace')}</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 px-3 py-1.5 text-[#E5E5E5]/70 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">{t('nav.home')}</span>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#C9A24D]/20 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-[#C9A24D]" />
                </div>
                <span className="text-[#E5E5E5] text-sm hidden sm:inline">
                  {user?.first_name}
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-[#E5E5E5]/60 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Payment Message Banner */}
        {paymentMessage && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            paymentMessage.type === 'success' 
              ? 'bg-green-500/20 border border-green-500/30' 
              : 'bg-orange-500/20 border border-orange-500/30'
          }`}>
            {paymentMessage.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-orange-400" />
            )}
            <span className={paymentMessage.type === 'success' ? 'text-green-400' : 'text-orange-400'}>
              {paymentMessage.text}
            </span>
            <button 
              onClick={() => setPaymentMessage(null)}
              className="ml-auto text-[#E5E5E5]/60 hover:text-[#E5E5E5]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Welcome Section - Mobile optimized */}
        <div className="mb-4 sm:mb-8">
          <h2 className="text-xl sm:text-3xl font-['Playfair_Display'] text-[#E5E5E5] mb-1 sm:mb-2">
            Bonjour, {user?.first_name} 👋
          </h2>
          <p className="text-sm sm:text-base text-[#E5E5E5]/60">
            Bienvenue dans votre espace personnel
          </p>
        </div>

        {/* Stats Cards - Mobile optimized */}
        <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
            <CardContent className="p-2.5 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-[#C9A24D]/10 rounded-lg">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-[#E5E5E5]">{stats.total}</p>
                  <p className="text-[10px] sm:text-xs text-[#E5E5E5]/60">Réservations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
            <CardContent className="p-2.5 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-[#E5E5E5]">{stats.completed}</p>
                  <p className="text-[10px] sm:text-xs text-[#E5E5E5]/60">Effectués</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
            <CardContent className="p-2.5 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-500/10 rounded-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-[#E5E5E5]">{stats.upcoming}</p>
                  <p className="text-[10px] sm:text-xs text-[#E5E5E5]/60">À venir</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
            <CardContent className="p-2.5 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-purple-500/10 rounded-lg">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-[#E5E5E5]">{stats.totalSpent.toFixed(0)}€</p>
                  <p className="text-[10px] sm:text-xs text-[#E5E5E5]/60">Dépensé</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs - Mobile optimized */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <TabsList className="bg-[#1F1F1F] border border-[#C9A24D]/20 p-1 w-full flex overflow-x-auto">
            <Link to="/" className="flex-shrink-0">
              <TabsTrigger 
                value="home" 
                className="data-[state=active]:bg-[#C9A24D] data-[state=active]:text-[#0B0B0B] text-xs sm:text-sm px-2 sm:px-3"
              >
                <Home className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('nav.home')}</span>
              </TabsTrigger>
            </Link>
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-[#C9A24D] data-[state=active]:text-[#0B0B0B] text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0"
            >
              <Home className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Aperçu</span>
            </TabsTrigger>
            <TabsTrigger 
              value="bookings"
              className="data-[state=active]:bg-[#C9A24D] data-[state=active]:text-[#0B0B0B] text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0"
            >
              <History className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Historique</span>
            </TabsTrigger>
            <TabsTrigger 
              value="profile"
              className="data-[state=active]:bg-[#C9A24D] data-[state=active]:text-[#0B0B0B] text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0"
            >
              <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Profil</span>
            </TabsTrigger>
            <TabsTrigger 
              value="documents"
              className="data-[state=active]:bg-[#C9A24D] data-[state=active]:text-[#0B0B0B] text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0"
              data-testid="tab-documents"
            >
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Documents</span>
            </TabsTrigger>
            <TabsTrigger 
              value="payments"
              className="data-[state=active]:bg-[#C9A24D] data-[state=active]:text-[#0B0B0B] text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0"
              data-testid="tab-payments"
            >
              <Euro className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Paiements</span>
            </TabsTrigger>
            <TabsTrigger 
              value="history"
              className="data-[state=active]:bg-[#C9A24D] data-[state=active]:text-[#0B0B0B] text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0"
              data-testid="tab-history"
            >
              <History className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Historique</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 sm:space-y-6">
            {/* Quick Actions */}
            <div className="grid grid-cols-1 gap-3 sm:gap-6">
              <Card className="bg-gradient-to-br from-[#C9A24D]/20 to-[#1F1F1F] border-[#C9A24D]/30">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-xl font-semibold text-[#E5E5E5] mb-1 sm:mb-2">
                        {t('common.bookDriver')}
                      </h3>
                      <p className="text-[#E5E5E5]/60 text-xs sm:text-sm mb-3 sm:mb-4">
                        Transport VTC premium en France
                      </p>
                      <Link to="/booking">
                        <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] text-xs sm:text-sm h-9 sm:h-10">
                          <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                          Nouvelle réservation
                        </Button>
                      </Link>
                    </div>
                    <Car className="w-12 h-12 sm:w-16 sm:h-16 text-[#C9A24D]/30 flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-xl font-semibold text-[#E5E5E5] mb-1 sm:mb-2">
                        {t('common.requestQuote')}
                      </h3>
                      <p className="text-[#E5E5E5]/60 text-xs sm:text-sm mb-3 sm:mb-4">
                        Excursions et trajets personnalisés
                      </p>
                      <Link to="/contact">
                        <Button variant="outline" className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 text-xs sm:text-sm h-9 sm:h-10">
                          <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                          Demander un devis
                        </Button>
                      </Link>
                    </div>
                    <MapPin className="w-12 h-12 sm:w-16 sm:h-16 text-[#C9A24D]/30 flex-shrink-0 ml-2" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Bookings */}
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
                <CardTitle className="text-[#E5E5E5] flex items-center gap-2 text-base sm:text-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                  Prochains trajets
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {loading ? (
                  <div className="text-center py-6 sm:py-8 text-[#E5E5E5]/60 text-sm">Chargement...</div>
                ) : upcomingBookings.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-[#E5E5E5]/20 mx-auto mb-3 sm:mb-4" />
                    <p className="text-[#E5E5E5]/60 text-sm">Aucun trajet à venir</p>
                    <Link to="/booking" className="inline-block mt-3 sm:mt-4">
                      <Button variant="outline" size="sm" className="border-[#C9A24D] text-[#C9A24D] text-xs sm:text-sm">
                        Réserver maintenant
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {upcomingBookings.slice(0, 3).map((booking) => (
                      <div 
                        key={booking.id}
                        onClick={() => setSelectedBooking(booking)}
                        className="flex items-center justify-between p-3 sm:p-4 bg-[#0B0B0B] rounded-lg border border-[#C9A24D]/10 hover:border-[#C9A24D]/40 cursor-pointer transition-all active:bg-[#0B0B0B]/80"
                      >
                        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                          <div className="p-2 sm:p-3 bg-[#C9A24D]/10 rounded-lg flex-shrink-0">
                            <Car className="w-4 h-4 sm:w-6 sm:h-6 text-[#C9A24D]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[#C9A24D] font-mono text-[10px] sm:text-xs mb-0.5 sm:mb-1">
                              {booking.booking_number || booking.id?.slice(-8).toUpperCase()}
                            </p>
                            <p className="text-[#E5E5E5] font-medium">
                              {booking.pickup?.address?.split(',')[0] || booking.pickup_address?.split(',')[0] || 'Départ'} → {booking.dropoff?.address?.split(',')[0] || booking.dropoff_address?.split(',')[0] || 'Arrivée'}
                            </p>
                            <p className="text-sm text-[#E5E5E5]/60">
                              {formatDate(booking.pickup_datetime)} à {formatTime(booking.pickup_datetime)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={STATUS_COLORS[booking.status]}>
                            {STATUS_LABELS[booking.status]}
                          </Badge>
                          <ChevronRight className="w-5 h-5 text-[#E5E5E5]/30" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings History Tab */}
          <TabsContent value="bookings" className="space-y-4 sm:space-y-6">
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-[#E5E5E5] flex items-center gap-2 text-base sm:text-lg">
                  <History className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                  Historique des réservations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {loading ? (
                  <div className="text-center py-6 sm:py-8 text-[#E5E5E5]/60 text-sm">Chargement...</div>
                ) : bookings.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <History className="w-10 h-10 sm:w-12 sm:h-12 text-[#E5E5E5]/20 mx-auto mb-3 sm:mb-4" />
                    <p className="text-[#E5E5E5]/60 text-sm">Aucune réservation</p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {bookings.map((booking) => (
                      <div 
                        key={booking.id}
                        onClick={() => setSelectedBooking(booking)}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-[#0B0B0B] rounded-lg border border-[#C9A24D]/10 hover:border-[#C9A24D]/40 cursor-pointer transition-all active:bg-[#0B0B0B]/80"
                      >
                        <div className="flex items-start sm:items-center gap-2.5 sm:gap-4 flex-1 min-w-0">
                          <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                            booking.status === 'completed' ? 'bg-green-500/10' :
                            booking.status === 'cancelled' ? 'bg-red-500/10' :
                            'bg-[#C9A24D]/10'
                          }`}>
                            {booking.status === 'completed' ? (
                              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                            ) : booking.status === 'cancelled' ? (
                              <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                            ) : (
                              <Car className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#C9A24D] font-mono text-[10px] sm:text-xs mb-0.5 sm:mb-1">
                              {booking.booking_number || booking.id?.slice(-8).toUpperCase()}
                            </p>
                            <p className="text-[#E5E5E5] text-sm sm:text-base font-medium truncate">
                              {booking.pickup?.address?.split(',')[0] || booking.pickup_address?.split(',')[0] || 'Départ'} → {booking.dropoff?.address?.split(',')[0] || booking.dropoff_address?.split(',')[0] || 'Arrivée'}
                            </p>
                            <p className="text-xs sm:text-sm text-[#E5E5E5]/60">
                              {formatDate(booking.pickup_datetime)} • {booking.vehicle_category || 'Berline'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-[#C9A24D]/10">
                          <Badge className={`${STATUS_COLORS[booking.status]} text-[10px] sm:text-xs`}>
                            {STATUS_LABELS[booking.status]}
                          </Badge>
                          <div className="flex items-center gap-2">
                            <span className="text-[#C9A24D] font-semibold text-sm sm:text-base">
                              {(booking.price?.total || booking.price?.total_ttc || 0).toFixed(0)}€
                            </span>
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#E5E5E5]/30" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 sm:space-y-6">
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-[#E5E5E5] flex items-center gap-2 text-base sm:text-lg">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                    Mon profil
                  </CardTitle>
                  {!editMode ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditMode(true)}
                      className="border-[#C9A24D] text-[#C9A24D] w-full sm:w-auto text-xs sm:text-sm h-9"
                    >
                      <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                      Modifier
                    </Button>
                  ) : (
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditMode(false)}
                        className="text-[#E5E5E5]/60 flex-1 sm:flex-none text-xs sm:text-sm h-9"
                      >
                        Annuler
                      </Button>
                      <Button 
                        size="sm"
                        onClick={handleSaveProfile}
                        disabled={saving}
                        className="bg-[#C9A24D] text-[#0B0B0B] flex-1 sm:flex-none text-xs sm:text-sm h-9"
                      >
                        <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="text-xs sm:text-sm text-[#E5E5E5]/60 mb-1.5 sm:mb-2 block">Prénom</label>
                    {editMode ? (
                      <Input 
                        value={profileData.first_name}
                        onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                        className="bg-[#0B0B0B] border-[#C9A24D]/20 text-[#E5E5E5] h-9 sm:h-10 text-sm"
                      />
                    ) : (
                      <p className="text-[#E5E5E5] font-medium text-sm sm:text-base">{user?.first_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm text-[#E5E5E5]/60 mb-1.5 sm:mb-2 block">Nom</label>
                    {editMode ? (
                      <Input 
                        value={profileData.last_name}
                        onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                        className="bg-[#0B0B0B] border-[#C9A24D]/20 text-[#E5E5E5] h-9 sm:h-10 text-sm"
                      />
                    ) : (
                      <p className="text-[#E5E5E5] font-medium text-sm sm:text-base">{user?.last_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm text-[#E5E5E5]/60 mb-1.5 sm:mb-2 block flex items-center gap-1.5 sm:gap-2">
                      <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Email
                    </label>
                    <p className="text-[#E5E5E5] font-medium text-sm sm:text-base break-all">{user?.email}</p>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm text-[#E5E5E5]/60 mb-1.5 sm:mb-2 block flex items-center gap-1.5 sm:gap-2">
                      <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Téléphone
                    </label>
                    {editMode ? (
                      <Input 
                        value={profileData.phone}
                        onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                        className="bg-[#0B0B0B] border-[#C9A24D]/20 text-[#E5E5E5] h-9 sm:h-10 text-sm"
                        placeholder="+33 6 12 34 56 78"
                      />
                    ) : (
                      <p className="text-[#E5E5E5] font-medium text-sm sm:text-base">{user?.phone || '-'}</p>
                    )}
                  </div>
                </div>
                
                <div className="pt-4 sm:pt-6 border-t border-[#C9A24D]/10">
                  <p className="text-[10px] sm:text-xs text-[#E5E5E5]/40">
                    Compte créé le {user?.created_at ? formatDate(user.created_at) : '-'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4 sm:space-y-6" data-testid="documents-tab">
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-[#E5E5E5] flex items-center gap-2 text-base sm:text-lg">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                  Mes documents
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {docsLoading ? (
                  <div className="text-center py-8 text-[#E5E5E5]/60 text-sm">Chargement...</div>
                ) : clientDocuments.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-[#E5E5E5]/20 mx-auto mb-4" />
                    <p className="text-[#E5E5E5]/60 text-sm">Aucun document disponible</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clientDocuments.map((doc) => {
                      const typeConfig = {
                        quote: { label: 'Devis', icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
                        transport_order: { label: 'Bon de transport', icon: Car, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
                        receipt: { label: 'Reçu', icon: CreditCard, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
                      }[doc.type] || { label: doc.type, icon: FileText, color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/20' };
                      const Icon = typeConfig.icon;
                      const statusCfg = {
                        sent: { label: 'Envoyé', cls: 'bg-blue-500/20 text-blue-400' },
                        accepted: { label: 'Accepté', cls: 'bg-emerald-500/20 text-emerald-400' },
                        generated: { label: 'Généré', cls: 'bg-[#C9A24D]/20 text-[#C9A24D]' },
                      }[doc.status] || { label: doc.status, cls: 'bg-gray-500/20 text-gray-400' };

                      return (
                        <div key={doc.id} className={`p-4 bg-[#0B0B0B] rounded-lg border ${typeConfig.border} flex items-center justify-between`} data-testid={`doc-${doc.id}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className={`p-2 rounded-lg ${typeConfig.bg}`}>
                              <Icon className={`w-4 h-4 ${typeConfig.color}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[#E5E5E5] font-medium text-sm">{doc.document_number}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusCfg.cls}`}>{statusCfg.label}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-[#E5E5E5]/50">
                                <span>{typeConfig.label}</span>
                                {doc.booking_ref && <span>Réf: {doc.booking_ref}</span>}
                                {doc.amount > 0 && <span className="text-[#C9A24D]">{doc.amount.toFixed(2)}€</span>}
                              </div>
                              <p className="text-[10px] text-[#E5E5E5]/30 mt-0.5">
                                {doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
                                {doc.signer_name && ` — Signé par ${doc.signer_name}`}
                              </p>
                            </div>
                          </div>
                          {doc.type === 'quote' && doc.public_token && doc.status !== 'accepted' && (
                            <a href={`/devis/${doc.public_token}`} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-[#C9A24D] hover:underline ml-3 shrink-0" data-testid={`view-quote-${doc.id}`}>
                              Voir le devis
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-4 sm:space-y-6" data-testid="payments-tab">
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-[#E5E5E5] flex items-center gap-2 text-base sm:text-lg">
                  <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                  Suivi des paiements
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {paymentsLoading ? (
                  <div className="text-center py-8 text-[#E5E5E5]/60 text-sm">Chargement...</div>
                ) : clientPayments.length === 0 ? (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-[#E5E5E5]/20 mx-auto mb-4" />
                    <p className="text-[#E5E5E5]/60 text-sm">Aucune réservation</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clientPayments.map((p) => {
                      const paySt = {
                        paye: { label: 'Payé', cls: 'bg-emerald-500/20 text-emerald-400' },
                        acompte_paye: { label: 'Acompte payé', cls: 'bg-amber-500/20 text-amber-400' },
                        non_paye: { label: 'Non payé', cls: 'bg-red-500/20 text-red-400' },
                      }[p.payment_status] || { label: p.payment_status || 'Non payé', cls: 'bg-gray-500/20 text-gray-400' };
                      const quoteSt = p.quote_status ? {
                        devis_envoye: { label: 'Devis envoyé', cls: 'bg-blue-500/15 text-blue-400' },
                        devis_accepte: { label: 'Devis accepté', cls: 'bg-emerald-500/15 text-emerald-400' },
                      }[p.quote_status] : null;

                      return (
                        <div key={p.mission_id} className="p-4 bg-[#0B0B0B] rounded-lg border border-[#C9A24D]/10" data-testid={`payment-${p.mission_id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[#C9A24D] font-mono text-xs">{p.booking_ref}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${paySt.cls}`}>{paySt.label}</span>
                              {quoteSt && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${quoteSt.cls}`}>{quoteSt.label}</span>}
                            </div>
                            <Badge className={STATUS_COLORS[p.status] || 'bg-gray-500/20 text-gray-400'}>
                              {STATUS_LABELS[p.status] || p.status}
                            </Badge>
                          </div>
                          <div className="text-xs text-[#E5E5E5]/50 mb-2">
                            {p.pickup_address?.split(',')[0]} → {p.dropoff_address?.split(',')[0]}
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-xs">
                              <span className="text-[#E5E5E5]/40">Total: <span className="text-[#E5E5E5]">{p.total_ttc?.toFixed(2)}€</span></span>
                              {p.amount_paid > 0 && <span className="text-emerald-400">Payé: {p.amount_paid?.toFixed(2)}€</span>}
                              {p.balance > 0 && <span className="text-amber-400">Solde: {p.balance?.toFixed(2)}€</span>}
                            </div>
                            {p.pickup_datetime && (
                              <span className="text-[10px] text-[#E5E5E5]/30">
                                {new Date(p.pickup_datetime).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4 sm:space-y-6" data-testid="history-tab">
            <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-[#E5E5E5] flex items-center gap-2 text-base sm:text-lg">
                  <History className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9A24D]" />
                  Historique
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {historyLoading ? (
                  <div className="text-center py-8 text-[#E5E5E5]/60 text-sm">Chargement...</div>
                ) : clientHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-[#E5E5E5]/20 mx-auto mb-4" />
                    <p className="text-[#E5E5E5]/60 text-sm">Aucun historique disponible</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-[#C9A24D]/20" />
                    <div className="space-y-4">
                      {clientHistory.map((evt, i) => {
                        const isDoc = evt.type === 'document';
                        const isEmail = evt.type === 'email';
                        return (
                          <div key={i} className="relative pl-8" data-testid={`history-event-${i}`}>
                            <div className={`absolute left-1.5 top-1 w-3 h-3 rounded-full border-2 border-[#0B0B0B] ${isDoc ? 'bg-[#C9A24D]' : isEmail ? 'bg-blue-400' : 'bg-gray-400'}`} />
                            <div className="p-3 bg-[#0B0B0B] rounded-lg border border-[#C9A24D]/10">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  {isDoc ? <FileText className="w-3.5 h-3.5 text-[#C9A24D]" /> : <Mail className="w-3.5 h-3.5 text-blue-400" />}
                                  <span className="text-[#E5E5E5] text-sm font-medium">{evt.label}</span>
                                </div>
                                {evt.booking_ref && <span className="text-[10px] text-[#C9A24D] font-mono">{evt.booking_ref}</span>}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-[#E5E5E5]/50">{evt.detail}</span>
                                <span className="text-[10px] text-[#E5E5E5]/30">
                                  {evt.date ? new Date(evt.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#C9A24D]/10 mt-auto py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-[#E5E5E5]/40 text-sm">
              © 2026 Y GO Shuttle Services France — {t('common.mySpace')}
            </p>
            <div className="flex items-center gap-4 text-sm">
              <a href="tel:+33278780404" className="text-[#E5E5E5]/60 hover:text-[#C9A24D] flex items-center gap-1">
                <Phone className="w-4 h-4" />
                +33 2 78 78 04 04
              </a>
              <a href="mailto:support@y-go.fr" className="text-[#E5E5E5]/60 hover:text-[#C9A24D] flex items-center gap-1">
                <Mail className="w-4 h-4" />
                support@y-go.fr
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onRefresh={async () => {
            // Refetch bookings and update selectedBooking with fresh data
            try {
              const token = localStorage.getItem('access_token');
              const response = await axios.get(`${API_URL}/api/bookings/my/list`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              const allBookings = response.data.bookings || [];
              setBookings(allBookings);
              
              // Update selected booking with fresh data from the server
              const updatedBooking = allBookings.find(b => b.id === selectedBooking.id);
              if (updatedBooking) {
                setSelectedBooking(updatedBooking);
              }
              
              // Update other state
              const now = new Date();
              const upcoming = allBookings.filter(b => 
                new Date(b.pickup_datetime) > now && 
                !['cancelled', 'completed'].includes(b.status)
              );
              setUpcomingBookings(upcoming);
              
              const completed = allBookings.filter(b => b.status === 'completed').length;
              const totalSpent = allBookings
                .filter(b => b.status === 'completed')
                .reduce((sum, b) => sum + (b.price?.total || 0), 0);
              
              setStats({
                total: allBookings.length,
                completed,
                upcoming: upcoming.length,
                totalSpent
              });
            } catch (error) {
              console.error('Failed to refresh bookings:', error);
            }
          }}
        />
      )}
    </div>
  );
};

export default ClientDashboard;
